import * as React from "react";
import { forwardRef, useCallback, useEffect, useState, useImperativeHandle, useRef } from "react";

import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Lightbulb,
  Mail,
  X,
} from "lucide-react";

import { cn } from "lib/utils";
import { useEmailValidation, UseEmailValidationOptions } from "hooks/useEmailValidation";
import { EmailSuggestion, EmailValidationStatus } from "utils/validators/emailValidation";
import { sanitizeEmailInput } from "utils/inputFilters";

// ============================================================================
// Types
// ============================================================================

export interface EmailInputProps
  extends Omit<React.ComponentProps<"input">, "onChange" | "value" | "type"> {
  /** Current value */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Blur handler (called after validation) */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when validation completes */
  onValidationChange?: (isValid: boolean, errorMessage: string | null) => void;
  /** Validation options */
  validationOptions?: UseEmailValidationOptions;
  /** Whether to show the email icon */
  showIcon?: boolean;
  /** Whether to show validation status indicators */
  showStatus?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** External error message (overrides internal validation) */
  error?: string;
  /** User data for "not advisor" validation */
  userData?: { email?: string; optional_email?: string } | null;
  /** Label text */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Whether to check for disposable emails */
  checkDisposable?: boolean;
  /** Custom class for the container */
  containerClassName?: string;
}

export interface EmailInputRef {
  /** Trigger validation manually */
  validate: () => Promise<boolean>;
  /** Clear validation state */
  clear: () => void;
  /** Get current validation status */
  getStatus: () => EmailValidationStatus;
  /** Focus the input */
  focus: () => void;
  /** Get the input element */
  getInputElement: () => HTMLInputElement | null;
}

// ============================================================================
// Animation Variants
// ============================================================================

const fadeInOut = {
  initial: { opacity: 0, y: -4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.15 },
};

const iconVariants = {
  initial: { scale: 0.8, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0.8, opacity: 0 },
  transition: { duration: 0.15 },
};

// ============================================================================
// Sub-components
// ============================================================================

interface StatusIconProps {
  status: EmailValidationStatus;
  isValidating: boolean;
}

const StatusIcon: React.FC<StatusIconProps> = ({ status, isValidating }) => {
  if (isValidating) {
    return (
      <motion.div {...iconVariants} key="loading">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </motion.div>
    );
  }

  switch (status) {
    case "valid":
      return (
        <motion.div {...iconVariants} key="valid">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        </motion.div>
      );
    case "invalid":
      return (
        <motion.div {...iconVariants} key="invalid">
          <AlertCircle className="h-4 w-4 text-destructive" />
        </motion.div>
      );
    case "unknown":
      return (
        <motion.div {...iconVariants} key="unknown">
          <CheckCircle2 className="h-4 w-4 text-amber-500" />
        </motion.div>
      );
    default:
      return null;
  }
};

interface SuggestionBannerProps {
  suggestion: EmailSuggestion;
  onAccept: () => void;
  onDismiss: () => void;
}

const SuggestionBanner: React.FC<SuggestionBannerProps> = ({
  suggestion,
  onAccept,
  onDismiss,
}) => (
  <motion.div
    {...fadeInOut}
    className="mt-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800/50 dark:bg-amber-950/30"
  >
    <Lightbulb className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
    <span className="flex-1 text-amber-800 dark:text-amber-200">
      ¿Quisiste decir{" "}
      <button
        type="button"
        onClick={onAccept}
        className="font-semibold underline underline-offset-2 transition-colors hover:text-amber-900 dark:hover:text-amber-100"
      >
        {suggestion.full}
      </button>
      ?
    </span>
    <button
      type="button"
      onClick={onDismiss}
      className="rounded p-0.5 text-amber-600 transition-colors hover:bg-amber-200/50 hover:text-amber-800 dark:text-amber-400 dark:hover:bg-amber-800/30 dark:hover:text-amber-200"
      aria-label="Descartar sugerencia"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  </motion.div>
);

interface ErrorMessageProps {
  message: string;
  id?: string;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ message, id }) => (
  <motion.p
    {...fadeInOut}
    id={id}
    className="mt-1.5 flex items-center gap-1.5 text-sm text-destructive"
    role="alert"
  >
    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
    <span>{message}</span>
  </motion.p>
);

// ============================================================================
// Main Component
// ============================================================================

const EmailInput = forwardRef<EmailInputRef, EmailInputProps>(
  (
    {
      value: externalValue,
      onChange,
      onBlur,
      onValidationChange,
      validationOptions = {},
      showIcon = true,
      showStatus = true,
      required = false,
      error: externalError,
      userData,
      label,
      helperText,
      checkDisposable = true,
      containerClassName,
      className,
      disabled,
      placeholder = "correo@ejemplo.com",
      id,
      ...props
    },
    ref
  ) => {
    // Local state for uncontrolled mode
    const [internalValue, setInternalValue] = useState("");
    const [hasFocused, setHasFocused] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    // Determine if controlled or uncontrolled
    const isControlled = externalValue !== undefined;
    const value = isControlled ? externalValue : internalValue;

    // Initialize validation hook
    const {
      status,
      isValidating,
      isValid,
      errorMessage,
      suggestion,
      validate,
      checkTypo,
      clear,
      acceptSuggestion,
      dismissSuggestion,
    } = useEmailValidation({
      required,
      checkDisposable,
      userData,
      debounceMs: 300,
      ...validationOptions,
      onValidationComplete: (result) => {
        if (onValidationChange) {
          onValidationChange(result.isValid, result.errorMessage);
        }
        validationOptions.onValidationComplete?.(result);
      },
    });

    // Display error (external takes precedence)
    const displayError = externalError || errorMessage;
    // Show error if: 1) external error is set (from form validation), or 2) field was focused and has internal error
    const showError = (externalError || (hasFocused && displayError)) && status !== "pending";

    // Determine visual state
    const isInvalid = showError || status === "invalid";
    // Only show valid state (green border) when showStatus is true (not in minimal mode)
    const isValidState = showStatus && !isInvalid && status === "valid";

    // Generate unique ID if not provided
    const inputId = id || React.useId();

    // Handle value change
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const sanitized = sanitizeEmailInput(e.target.value);

        if (!isControlled) {
          setInternalValue(sanitized);
        }

        onChange?.(sanitized);

        // Check for typos as user types (after basic format is valid)
        if (sanitized.includes("@") && sanitized.includes(".")) {
          checkTypo(sanitized);
        }
      },
      [isControlled, onChange, checkTypo]
    );

    // Handle blur - trigger validation
    const handleBlur = useCallback(
      async (e: React.FocusEvent<HTMLInputElement>) => {
        setHasFocused(true);
        await validate(value);
        onBlur?.(e);
      },
      [validate, value, onBlur]
    );

    // Handle accepting suggestion
    const handleAcceptSuggestion = useCallback(() => {
      const suggestedEmail = acceptSuggestion();
      if (suggestedEmail) {
        if (!isControlled) {
          setInternalValue(suggestedEmail);
        }
        onChange?.(suggestedEmail);
        // Re-validate with new value
        validate(suggestedEmail);
      }
    }, [acceptSuggestion, isControlled, onChange, validate]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        validate: async () => {
          setHasFocused(true);
          const result = await validate(value);
          return result.isValid;
        },
        clear: () => {
          clear();
          setHasFocused(false);
        },
        getStatus: () => status,
        focus: () => inputRef.current?.focus(),
        getInputElement: () => inputRef.current,
      }),
      [validate, clear, status, value]
    );

    // Reset validation when value changes externally
    useEffect(() => {
      if (isControlled && externalValue === "") {
        clear();
        setHasFocused(false);
      }
    }, [isControlled, externalValue, clear]);

    return (
      <div className={cn("w-full", containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {label}
            {required && <span className="ml-0.5 text-destructive">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {showIcon && (
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              <Mail
                className={cn(
                  "h-4 w-4 transition-colors",
                  isInvalid
                    ? "text-destructive"
                    : isValidState
                      ? "text-emerald-500"
                      : "text-muted-foreground"
                )}
              />
            </div>
          )}

          {/* Input */}
          <input
            ref={inputRef}
            id={inputId}
            type="email"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={isInvalid}
            aria-describedby={
              showError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            data-sentry-ignore
            className={cn(
              // Base styles
              "flex h-10 w-full rounded-lg border bg-background px-3 py-2 text-sm shadow-sm transition-all duration-200",
              "placeholder:text-muted-foreground",
              "focus:outline-none focus:ring-2 focus:ring-offset-0",
              "disabled:cursor-not-allowed disabled:opacity-50",
              // Icon padding
              showIcon && "pl-10",
              showStatus && "pr-10",
              // State-based styles
              isInvalid
                ? "border-destructive/50 focus:border-destructive focus:ring-destructive/20"
                : isValidState
                  ? "border-emerald-300 focus:border-emerald-400 focus:ring-emerald-200/50 dark:border-emerald-700 dark:focus:border-emerald-600"
                  : "border-input focus:border-primary focus:ring-primary/20",
              // Validating state
              isValidating && "border-primary/50",
              className
            )}
            {...props}
          />

          {/* Right Status Icon */}
          {showStatus && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <AnimatePresence mode="wait">
                <StatusIcon status={status} isValidating={isValidating} />
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Error Message */}
        <AnimatePresence mode="wait">
          {showError && (
            <ErrorMessage message={displayError!} id={`${inputId}-error`} key="error" />
          )}
        </AnimatePresence>

        {/* Suggestion Banner */}
        <AnimatePresence>
          {suggestion && !showError && (
            <SuggestionBanner
              key="suggestion"
              suggestion={suggestion}
              onAccept={handleAcceptSuggestion}
              onDismiss={dismissSuggestion}
            />
          )}
        </AnimatePresence>

        {/* Helper Text */}
        {helperText && !showError && !suggestion && (
          <p
            id={`${inputId}-helper`}
            className="mt-1.5 text-sm text-muted-foreground"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

EmailInput.displayName = "EmailInput";

export { EmailInput };
